import { useI18N, useResponsive, useTheme } from "@sundae/react-hooks";
import { ADA_METADATA } from "@sundaeswap/core";
import transparentize from "polished/lib/color/transparentize";
import { useCallback } from "react";
import { TTasteTestTargetPriceChartConfig } from "../../components/TasteTestComponents/TasteTestTargetPriceChart/TasteTestTargetPriceChart";
import { Asset, TokenStats } from "../../gql/generated/taste-test.sdk";
import { useTasteTestContext } from "../../pages/TasteTestDetail/context";
import useTargetPrice from "../../pages/TasteTestDetail/hooks/useTargetPrice";
import { slotToUnix } from "../../utils/slots.utils";
import { calculateTargetPrice } from "../../utils/taste-test.utils";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");

const defaultPrimaryColor = colors.primary["DEFAULT"];
const defaultSecondaryColor = colors.secondary["DEFAULT"];

export const useTasteTestTargetPriceChart = () => {
  const { theme } = useTheme();
  const responsive = useResponsive();
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: {
      tasteTest,
      tasteTestStatus: { isTasteTestPending },
      tasteTestPersonalization,
    },
  } = useTasteTestContext();
  const { targetPrice } = useTargetPrice();
  const isDark = theme === "dark";
  const primaryChartColor = tasteTestPersonalization?.primaryColor || defaultPrimaryColor;
  const secondaryChartColor = tasteTestPersonalization?.secondaryColor || defaultSecondaryColor;

  /**
   * This data is used to mock sample chart data once a taste test is completed/draft, etc.
   */
  const getOngoingTasteTestMockData = useCallback(() => {
    return Array.from({ length: 10 }).map(() => Math.floor(Math.random() * 8999 + 1000));
  }, []);

  /**
   * These labels are used to render the correct labels on the chart axis.
   */
  const getChartLabels = useCallback(() => {
    return (
      tasteTest?.historicalBalance?.times.map((time) => {
        return new Date(slotToUnix(time) * 1000).getTime();
      }) ?? []
    );
  }, [tasteTest?.historicalBalance?.times]);

  /**
   * This returns the taste test balance over time for both the token gathered and token supplied.
   */
  const getMemoizedTasteTestBalance = useCallback(
    (slot: string, index: number): TokenStats => {
      return {
        tokenGathered: {
          amount: tasteTest?.historicalBalance?.tokenGathered?.[index] ?? "0",
          asset:
            tasteTest?.currentBalance?.tokenGathered?.asset ?? (ADA_METADATA as unknown as Asset),
        },
        tokenSupplied: {
          amount: tasteTest?.currentBalance?.tokenSupplied?.amount ?? "0",
          asset: tasteTest?.currentBalance?.tokenSupplied?.asset ?? ({} as Asset),
        },
        timestamp: { slot, format: new Date(slotToUnix(slot) * 1000).toISOString() },
      };
    },
    [
      tasteTest?.historicalBalance,
      tasteTest?.currentBalance?.tokenGathered?.asset,
      tasteTest?.currentBalance?.tokenSupplied?.amount,
      tasteTest?.currentBalance?.tokenSupplied?.asset,
    ]
  );

  /**
   * This returns the chart data for the target price.
   */
  const getChartData = useCallback(() => {
    return tasteTest?.historicalBalance?.times?.map((slot, index) => ({
      x: new Date(slotToUnix(slot) * 1000).getTime(), // Getting time in milliseconds
      y: calculateTargetPrice(getMemoizedTasteTestBalance(slot, index))?.raw,
    }));
  }, [tasteTest?.historicalBalance?.times, getMemoizedTasteTestBalance]);

  // Flag to determine if the taste test has target price information.
  const hasTargetPriceInformation = !!getChartData()?.length;

  /**
   * Chart config that's used to render the chart and its options.
   */
  const getChartConfig = useCallback(() => {
    return {
      currentLocale,
      isDark,
      minX: getChartLabels()[0],
      maxX: getChartLabels()[getChartLabels().length - 1],
      responsive,
      t,
    } as TTasteTestTargetPriceChartConfig;
  }, [currentLocale, isDark, getChartLabels, t]);

  /**
   * This returns the chart datasets for the target price.
   */
  const getChartDatasets = useCallback(() => {
    const chartData = getChartData();
    return {
      datasets: [
        {
          label: `${t("detail.priceChart.title")} (₳)`,
          data:
            isTasteTestPending || !hasTargetPriceInformation
              ? getOngoingTasteTestMockData()
              : chartData,
          borderColor: primaryChartColor,
          backgroundColor: transparentize(isDark ? 0.8 : 0.7, primaryChartColor),
          pointBackgroundColor: primaryChartColor,
          fill: true,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          order: 2,
        },
        ...(targetPrice
          ? [
              {
                label: `${t("targetPrice")} (₳)`,
                data: chartData
                  ? [
                      { ...chartData[0], y: targetPrice },
                      { ...chartData[chartData.length - 1], y: targetPrice },
                    ]
                  : [],
                borderColor: transparentize(isDark ? 0.2 : 0.3, secondaryChartColor),
                pointRadius: 0,
                pointHoverRadius: 0,
                order: 1,
              },
            ]
          : []),
      ],
    };
  }, [
    getChartData,
    getOngoingTasteTestMockData,
    hasTargetPriceInformation,
    isTasteTestPending,
    isDark,
    t,
    targetPrice,
    tasteTestPersonalization?.primaryColor,
  ]);

  return {
    chartConfig: getChartConfig(),
    chartData: getChartDatasets(),
    hasTargetPriceInformation,
    responsive,
  };
};
